import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  private readonly TOKEN_KEY = 'auth-token';
  private readonly REFRESH_TOKEN_KEY = 'refresh-token';
  private readonly USER_KEY = 'auth-user';
  private readonly ROLE_KEY = 'auth-role';
  private readonly SCHOOL_NAME_KEY = 'school-display-name';
  /** Same as school id from `schools` table when tenant-per-school. */
  private readonly TENANT_ID_KEY = 'tenant-id';

  constructor() { }

  signOut(): void {
    window.localStorage.clear();
  }

  public saveToken(token: string): void {
    window.localStorage.removeItem(this.TOKEN_KEY);
    window.localStorage.setItem(this.TOKEN_KEY, token);
  }

  public getToken(): string | null {
    return window.localStorage.getItem(this.TOKEN_KEY);
  }

  public saveRefreshToken(token: string): void {
    window.localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    window.localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  public getRefreshToken(): string | null {
    return window.localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  public saveUser(user: any): void {
    window.localStorage.removeItem(this.USER_KEY);
    window.localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  public getUser(): any {
    const user = window.localStorage.getItem(this.USER_KEY);
    if (user) {
      return JSON.parse(user);
    }
    return null;
  }

  public saveRole(role: string): void {
    window.localStorage.removeItem(this.ROLE_KEY);
    window.localStorage.setItem(this.ROLE_KEY, role);
  }

  public getRole(): string | null {
    return window.localStorage.getItem(this.ROLE_KEY);
  }

  public isLoggedIn(): boolean {
    return !!this.getToken();
  }

  public isStudent(): boolean {
    return this.getRole() === 'ROLE_STUDENT';
  }

  public isTeacher(): boolean {
    return this.getRole() === 'ROLE_TEACHER';
  }

  public isAdmin(): boolean {
    return this.getRole() === 'ROLE_ADMIN';
  }

  public saveSchoolDisplayName(name: string): void {
    if (!name || !name.trim()) {
      return;
    }
    window.localStorage.setItem(this.SCHOOL_NAME_KEY, name.trim());
  }

  public getSchoolDisplayName(): string | null {
    return window.localStorage.getItem(this.SCHOOL_NAME_KEY);
  }

  public saveTenantId(id: number): void {
    if (id == null || Number.isNaN(id)) {
      return;
    }
    window.localStorage.setItem(this.TENANT_ID_KEY, String(id));
  }

  /** School / tenant id from login (required for onboarding users in this school). */
  public getTenantId(): number | null {
    const raw = window.localStorage.getItem(this.TENANT_ID_KEY);
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }
}
